import { CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { ROTULO_ESTILO, ROTULO_SEMANA, PAPEIS } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useEditor } from '../EstadoEditor';
import { TituloPasso } from '../ui';

export function PassoRevisao() {
  const { config, documento, paleta, atualizar } = useEditor();
  const papel = PAPEIS[config.formato];

  const linhas: Array<[string, string]> = [
    ['Formato', `${config.formato} · ${papel.larguraMm} × ${papel.alturaMm} mm`],
    [
      'Dias por página',
      config.fimDeSemanaDividido && config.paginasDeDia === 'DIA' && config.diasPorPagina === 1
        ? '1 dia por página, com o fim de semana dividido'
        : `${config.diasPorPagina} dia(s)`,
    ],
    ['Dias da semana', ROTULO_SEMANA[config.semana]],
    ['Período', documento.resumo.periodo],
    ['Estilo', ROTULO_ESTILO[config.estilo]],
    ['Tema', config.temaId === 'PERSONALIZADO' ? 'Personalizado' : config.temaId],
    ['Cor principal', paleta.principal],
    ['Harmonia', paleta.esquemaAplicado.replace(/_/g, ' ').toLowerCase()],
    ['Contraste do texto', `${paleta.contrasteTexto}:1`],
    ['Área de escrita por dia', `${documento.resumo.alturaEscritaMm} mm`],
    ['Páginas de cadernos', `${documento.resumo.cadernos} caderno(s) · ${documento.resumo.paginasDeCaderno} páginas`],
    ['Camadas de imagem', config.camadasImagem.length > 0 ? `${config.camadasImagem.length} camada(s)` : 'nenhuma'],
    ['Dados pessoais', config.dadosPessoais.nome || 'não informados'],
    ['Total de páginas', String(documento.resumo.totalPaginas)],
  ];

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Revisão final"
        descricao="Confira o resumo, navegue pela pré-visualização ao lado e depois salve na sua área ou gere o PDF."
      />

      <Campo rotulo="Nome do miolo" dica="É o nome que aparece na sua lista de miolos e na capa.">
        <Input value={config.nome} onChange={(e) => atualizar({ nome: e.target.value })} maxLength={70} />
      </Campo>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo} className="flex items-baseline justify-between gap-3 border-b border-dashed border-border/70 py-1.5 last:border-0">
            <dt className="text-xs text-muted-foreground">{rotulo}</dt>
            <dd className="text-right text-xs font-medium capitalize text-foreground">{valor}</dd>
          </div>
        ))}
      </dl>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Checagem técnica</h3>
        {documento.avisos.length === 0 ? (
          <p className="flex items-center gap-2 rounded-md border border-border bg-muted/60 p-3 text-xs text-muted-foreground">
            <CheckCircle2 className="size-3.5 text-success" />
            Nenhum alerta: contraste, espaço de escrita e paginação estão dentro do recomendado.
          </p>
        ) : (
          <ul className="space-y-2">
            {documento.avisos.map((aviso, i) => (
              <li
                key={i}
                className={[
                  'flex gap-2 rounded-md border p-3 text-xs leading-relaxed',
                  aviso.nivel === 'ATENCAO'
                    ? 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
                    : 'border-border bg-muted/60 text-muted-foreground',
                ].join(' ')}
              >
                {aviso.nivel === 'ATENCAO' ? (
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                ) : (
                  <Info className="mt-0.5 size-3.5 shrink-0" />
                )}
                <span>{aviso.mensagem}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        <Badge tom="principal">{documento.resumo.totalPaginas} páginas</Badge>
        <Badge>{documento.resumo.paginasDeDia} dias</Badge>
        <Badge>{papel.rotulo}</Badge>
      </div>
    </div>
  );
}
